import React, { useState, useCallback } from 'react';
import { Modal, Button, Icon, RosterSearch, type SelectedInvitee } from '../../../components/common';
import { UniversalFilePicker } from '../../../components/common/UniversalFilePicker';
import { useAppStore } from '../../../store/useAppStore';
import { useJobProgress } from '../../../contexts/job-progress-hooks';
import { triggerScriptAnalysis } from '../../sync/ai-api';
import { showToast } from '../../../utils/notifications';
import { logger } from '../../../utils/logger';
import { callBackendCommand, firebaseFunctionCommands } from '@/backend';
import type { FileNode } from '../../files/types';
import { queuePersonnelForInvite } from '../../invitations/queue-personnel';

// ============================================================================
// GettingStartedWizard — SM first-visit onboarding (2-step modal)
// ============================================================================
// Conditions: MANAGE_PROJECT permission + !member.onboardingDismissed + acts.length === 0
// Step 1: Import script (skip-able)
// Step 2: Invite team via RosterSearch (skip-able)
// Dismiss: sets onboardingDismissed on member doc

type WizardStep = 'script' | 'invite';

interface GettingStartedWizardProps {
    /** External open control — parent decides whether to show */
    isOpen: boolean;
    /** Called when wizard is dismissed */
    onDismiss: () => void;
}

export const GettingStartedWizard: React.FC<GettingStartedWizardProps> = ({
    isOpen,
    onDismiss,
}) => {
    const orgId = useAppStore((s) => s.cloud.orgId);
    const projectId = useAppStore((s) => s.cloud.projectId);
    const member = useAppStore((s) => s.cloud.member);
    const addPersonnel = useAppStore((s) => s.personnelActions.addPersonnel);
    const updateOperationalPersonnel = useAppStore((s) => s.personnelActions.updateOperationalPersonnel);
    const currentUser = useAppStore((s) => s.auth.user);
    const updateMember = useAppStore((s) => s.adminActions.updateMember);
    const { startJobTracking } = useJobProgress();

    const [step, setStep] = useState<WizardStep>('script');
    const [showFilePicker, setShowFilePicker] = useState(false);
    const [invitees, setInvitees] = useState<SelectedInvitee[]>([]);
    const [queueing, setQueueing] = useState(false);
    const [scriptStarted, setScriptStarted] = useState(false);

    // ── Dismiss Logic ────────────────────────────────────────────────
    const dismiss = useCallback(async () => {
        if (member) {
            updateMember(member.userId, { onboardingDismissed: true });
        }

        if (orgId && projectId && member?.userId) {
            try {
                await callBackendCommand(firebaseFunctionCommands.dismissProjectMemberOnboarding, {
                    orgId,
                    projectId,
                    memberUserId: member.userId,
                });
            } catch (error: unknown) {
                logger.error('Failed to persist onboardingDismissed:', error);
                // Non-fatal - local state already updated
            }
        }

        onDismiss();
    }, [orgId, projectId, member, updateMember, onDismiss]);

    // ── Step 1: Script Import ────────────────────────────────────────
    const handleScriptSelected = useCallback(
        async (files: FileNode[]) => {
            const file = files[0];
            const storagePath = file?.storagePath;
            if (!storagePath || !orgId || !projectId) return;

            try {
                const { clearStructure, setActiveJobId } = useAppStore.getState().showStructureActions;
                setActiveJobId('pending-script-import');
                clearStructure();
                setActiveJobId('pending-script-import');

                showToast('Starting script analysis...', 'info');
                const scriptTextAccess = useAppStore.getState().cloud.scriptTextAccess ?? false;
                const { jobId } = await triggerScriptAnalysis({ orgId, projectId }, storagePath, {
                    scriptTextAccess,
                    importMode: 'replace',
                    analyticsSource: 'onboarding',
                });
                setActiveJobId(jobId);
                startJobTracking({ orgId, projectId }, jobId);
                setScriptStarted(true);
                setShowFilePicker(false);
                setStep('invite'); // Auto-advance to invite step
            } catch (error: unknown) {
                const msg = error instanceof Error ? error.message : 'Failed to analyze script';
                useAppStore.getState().showStructureActions.setActiveJobId(null);
                logger.error('Script analysis failed:', error);
                showToast(msg, 'error');
            }
        },
        [orgId, projectId, startJobTracking]
    );

    const handleScriptUploaded = useCallback(
        (file: { storagePath: string; name: string; jobId?: string }) => {
            if (!orgId || !projectId) return;

            const { clearStructure, setActiveJobId } = useAppStore.getState().showStructureActions;
            clearStructure();
            setActiveJobId(file.jobId ?? file.storagePath);

            startJobTracking({ orgId, projectId }, file.jobId, file.storagePath);
            setScriptStarted(true);
            setShowFilePicker(false);
            setStep('invite');
        },
        [orgId, projectId, startJobTracking]
    );

    // ── Step 2: Queue Invites ────────────────────────────────────────
    const inviteReadyCount = invitees.filter((inv) => inv.name.trim() && inv.email.trim()).length;

    const handleQueueInvitees = useCallback(async () => {
        if (inviteReadyCount === 0) return;

        setQueueing(true);
        try {
            const queuedBy = member?.userId ?? currentUser?.uid;
            const validInvitees = invitees.filter((inv) => inv.name.trim() && inv.email.trim());
            for (const inv of validInvitees) {
                queuePersonnelForInvite({
                    personnel: useAppStore.getState().personnel,
                    addPersonnel,
                    updateOperationalPersonnel,
                }, {
                    name: inv.name,
                    email: inv.email,
                    category: inv.category,
                    rosterPersonId: inv.rosterPersonId,
                }, queuedBy);
            }

            showToast(
                `Added ${inviteReadyCount} people to the invite queue. Assign app access and send from Communications.`,
                'success'
            );

            await dismiss();
        } catch (error: unknown) {
            const msg = error instanceof Error ? error.message : 'Failed to queue invitations';
            logger.error('Invite queueing failed:', error);
            showToast(msg, 'error');
        } finally {
            setQueueing(false);
        }
    }, [
        addPersonnel,
        currentUser?.uid,
        inviteReadyCount,
        invitees,
        dismiss,
        member?.userId,
        updateOperationalPersonnel,
    ]);

    // ── Render ────────────────────────────────────────────────────────
    if (!isOpen) return null;

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={() => { void dismiss(); }}
                title={step === 'script' ? 'Welcome to Your Show!' : 'Invite Your Team'}
                size="lg"
            >
                <div className="space-y-5">
                    {/* Step Indicator */}
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-100 dark:border-gray-800">
                        <StepDot active={step === 'script'} completed={step === 'invite'} label="1" />
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                        <StepDot active={step === 'invite'} completed={false} label="2" />
                    </div>

                    {step === 'script' ? (
                        /* ── Step 1: Script Import ── */
                        <div className="text-center space-y-4 py-2">
                            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                                <Icon name="description" className="text-primary text-3xl" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Import Your Script
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
                                    Upload your show&apos;s script (PDF) and we&apos;ll automatically extract scenes, acts, characters, and more.
                                </p>
                            </div>

                            {scriptStarted ? (
                                <div className="flex items-center gap-2 justify-center text-sm text-green-600 dark:text-green-400 font-medium">
                                    <Icon name="check_circle" className="text-lg" />
                                    Script analysis started! You can track progress in the toolbar.
                                </div>
                            ) : (
                                <Button
                                    variant="primary"
                                    size="lg"
                                    onClick={() => setShowFilePicker(true)}
                                    leftIcon="upload_file"
                                >
                                    Choose Script PDF
                                </Button>
                            )}

                            <div className="flex items-center justify-center gap-4 pt-2">
                                <Button
                                    onClick={() => setStep('invite')}
                                    variant="link"
                                    size="none"
                                    className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                >
                                    Skip for now →
                                </Button>
                            </div>
                        </div>
                    ) : (
                        /* ── Step 2: Invite Team ── */
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center shrink-0">
                                    <Icon name="group_add" className="text-indigo-600 dark:text-indigo-400 text-xl" />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                        Invite Cast & Crew
                                    </h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Search your organization&apos;s roster or add people manually.
                                    </p>
                                </div>
                            </div>

                            <RosterSearch
                                orgId={orgId}
                                selectedInvitees={invitees}
                                onChange={setInvitees}
                                placeholder="Search roster by name..."
                            />

                            {/* Action Bar */}
                            <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800">
                                <Button
                                    onClick={() => setStep('script')}
                                    variant="link"
                                    size="none"
                                    className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                >
                                    ← Back
                                </Button>
                                <div className="flex items-center gap-3">
                                    <Button variant="ghost" onClick={() => { void dismiss(); }} disabled={queueing}>
                                        Skip
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={() => { void handleQueueInvitees(); }}
                                        disabled={queueing || inviteReadyCount === 0}
                                        leftIcon={queueing ? 'hourglass_empty' : 'person_add'}
                                    >
                                        {queueing
                                            ? 'Adding...'
                                            : `Add ${inviteReadyCount} to invite queue`}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Script File Picker (separate modal layer) */}
            <UniversalFilePicker
                isOpen={showFilePicker}
                onClose={() => setShowFilePicker(false)}
                accept="application/pdf"
                mode="single"
                title="Import Script (PDF)"
                onSelect={(files) => { void handleScriptSelected(files); }}
                onScriptUpload={handleScriptUploaded}
                scriptMode={true}
            />
        </>
    );
};

// ── Step dot indicator ──────────────────────────────────────────────
const StepDot: React.FC<{ active: boolean; completed: boolean; label: string }> = ({
    active,
    completed,
    label,
}) => (
    <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
            completed
                ? 'bg-green-500 text-white'
                : active
                ? 'bg-primary text-white shadow-md shadow-primary/30'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
        }`}
    >
        {completed ? <Icon name="check" className="text-sm" /> : label}
    </div>
);
